import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import {
	Container,
	Stack,
	AppBar,
	Toolbar,
	Typography,
	Dialog,
	IconButton,
	TableContainer,
	Table,
	TableCell,
	TableRow,
	TableHead,
	TableBody,
	Divider
} from '@mui/material';
import { isEmpty } from 'lodash';

import { useParams } from 'react-router-dom';
import { DataContext } from '../../../context/DataContext';

import { getRol, getToken, getUser } from '../../../api/Account';
import ReactToPrint from 'react-to-print';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import NoData from '../../../components/NoData';
import { getSaldoCarteraByGestorAsync } from '../../../api/Report';
import { PrintReport } from '../../../components/PrintReport';
import moment from 'moment';
import {
	getGestoresAsync,
	getGestoresBySucursalAccessAsync,
	getSucursalByUserAsync
} from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx/xlsx.mjs';

const SaldosCarteraByGestor = () => {
	const [data, setData] = useState([]);

	const [sucursalName, setSucursalName] = useState('');
	const [sucursalList, setSucursalList] = useState([]);

	const [gestorList, setGestorList] = useState([]);
	const [gestorName, setGestorName] = useState('');

	const compRef = useRef();

	const { selectedSucursal, selectedGestor, moneda, tipoCredito } = useParams();
	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const isDolar = parseInt(moneda) === 2;

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const formatMoney = (v) =>
		new Intl.NumberFormat(isDolar ? 'en-US' : 'es-NI', {
			style: 'currency',
			currency: isDolar ? 'USD' : 'NIO'
		}).format(v);

	const requestPayload = useMemo(
		() => ({
			moneda,
			gestorId: selectedGestor === 't' ? 0 : Number(selectedGestor),
			sucursalId: selectedSucursal === 't' ? 0 : Number(selectedSucursal),
			tipoCredito: tipoCredito === 't' ? 0 : Number(tipoCredito)
		}),
		[moneda, selectedGestor, selectedSucursal, tipoCredito]
	);

	const fetchData = useCallback(async () => {
		let sucursals = await getSucursals();

		if (selectedSucursal === 't') {
			setSucursalName('Todas');
		} else {
			const suc = sucursals.find((s) => s.id.toString() === selectedSucursal);
			setSucursalName(suc?.description ?? '');
		}

		setIsLoading(true);

		const result = await getSaldoCarteraByGestorAsync(token, requestPayload);

		setIsLoading(false);

		if (!result.statusResponse) {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setData(result.data.result);
	}, [requestPayload, selectedSucursal, token, setIsLoading, setIsUnautorized]);

	const getSucursals = async () => {
		setIsLoading(true);
		const result = await getSucursalByUserAsync(token);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}
		setIsLoading(false);
		setSucursalList(
			selectedSucursal === 't'
				? result.data.result
				: result.data.result.filter((g) => g.id.toString() === selectedSucursal)
		);

		let gestores = await getGestores(selectedSucursal);

		if (selectedGestor === 't') {
			setGestorName('Todos');
		} else {
			let name = gestores.filter((item) => item.id.toString() === selectedGestor);

			setGestorName(name[0].fullName);
		}
		return result.data.result;
	};

	const getGestores = async (sucursal) => {
		setIsLoading(true);
		const result =
			sucursal === 't'
				? await getGestoresBySucursalAccessAsync(token)
				: await getGestoresAsync(token, sucursal);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		setIsLoading(false);
		setGestorList(result.data.result);
		return result.data.result;
	};

	const hasFetchedRef = useRef(false);

	useEffect(() => {
		if (hasFetchedRef.current) return;
		hasFetchedRef.current = true;

		fetchData();
	}, [fetchData]);

	const pageStyle = `
    @media print {
      html, body {
        height: initial !important;
        overflow: initial !important;
        -webkit-print-color-adjust: exact;
      }
    }
    @media all {
      .pagebreak {
        display: none;
      }
    }
    @media print {
      .pagebreak {
        page-break-before: always;
      }
    }  
    @page { size: landscape;  margin: 3mm; }
  `;

	const exportExcel = () => {
		let exp = [];
		data.map((item) => {
			const {
				creditoPromedio,
				dia,
				gestor,
				montoDia,
				montoMora,
				montoVencido,
				montoVigente,
				mora,
				noClients,
				noCredits,
				saldoCartera,
				vencido,
				vigente
			} = item;

			let obj = {
				gestor,
				noClients,
				noCredits,
				dia,
				montoDia,
				percentDia: ((dia / noCredits) * 100).toFixed(2),
				mora,
				montoMora,
				percentMora: ((mora / noCredits) * 100).toFixed(2),
				montoMora,
				vigente,
				montoVigente,
				percentVigente: ((vigente / noCredits) * 100).toFixed(2),
				vencido,
				montoVencido,
				percentVencido: ((vencido / noCredits) * 100).toFixed(2),
				creditoPromedio,
				saldoCartera
			};
			exp.push(obj);
		});

		var bk = XLSX.utils.book_new(),
			bs = XLSX.utils.json_to_sheet(exp);

		XLSX.utils.book_append_sheet(bk, bs, 'saldosCarteraByGestor');
		XLSX.writeFile(bk, 'SaldosCarteraByGestor.xlsx');
	};

	const paramText = (
		<Container maxWidth={false} sx={{ textAlign: 'center' }}>
			<Stack direction={'row'} justifyContent="space-between">
				<Stack>
					<Typography
						sx={{
							color: '#2196f3',
							textAlign: 'left'
						}}
						fontSize={11}
					>
						Parametros:
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Sucursal: ${selectedSucursal === 't' ? 'Todas' : sucursalName}`}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Gestor Seleccionado: ${selectedGestor === 't' ? 'Todos' : gestorName}`}
					</Typography>

					<Typography fontSize={11} textAlign="left">
						{`Moneda: ${
							parseInt(moneda) === 1 ? 'Cordoba' : 'Dolar'
						} - Tipo de Crédito: ${
							tipoCredito === 't'
								? 'Todos'
								: tipoCredito === '1'
								? 'EMPEÑO'
								: 'CREDITO'
						}`}
					</Typography>
				</Stack>

				<Stack>
					<Typography
						sx={{
							color: '#2196f3',
							textAlign: 'left'
						}}
						fontSize={11}
					>
						Usuario:
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{user}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{rol}
					</Typography>
					<Typography fontSize={11} textAlign="left">
						{`Fecha y Hora: ${moment().format('DD/MM/YYYY hh:mm A')}`}
					</Typography>
				</Stack>
			</Stack>
		</Container>
	);

	const DataTable = ({ dataGestor = [], headerFontSize, dataFontSize, isForPrint = false }) => {
		const columns = [
			{
				key: 'gestor',
				label: 'Gestor',
				align: 'left',
				getValue: (r) => r.gestor
			},
			{
				key: 'noClients',
				label: 'No Clientes',
				align: 'center',
				getValue: (r) => r.noClients
			},
			{
				key: 'noCredits',
				label: 'No Creditos',
				align: 'center',
				getValue: (r) => r.noCredits
			},
			{
				key: 'dia',
				label: 'Al Dia',
				align: 'center',
				getValue: (r) => r.dia
			},
			{
				key: 'montoDia',
				label: 'M. al Dia',
				align: 'center',
				getValue: (r) => formatMoney(r.montoDia)
			},
			{
				key: 'dia%',
				label: '% al Dia',
				align: 'center',
				getValue: (r) => ((r.dia / r.noCredits) * 100).toFixed(2)
			},

			{
				key: 'mora',
				label: 'Mora',
				align: 'center',
				getValue: (r) => r.mora
			},
			{
				key: 'montoMora',
				label: 'M. Mora',
				align: 'center',
				getValue: (r) => formatMoney(r.montoMora)
			},
			{
				key: 'mora%',
				label: '% Mora',
				align: 'center',
				getValue: (r) => ((r.mora / r.noCredits) * 100).toFixed(2)
			},
			{
				key: 'vigente',
				label: 'Vigente',
				align: 'center',
				getValue: (r) => r.vigente
			},
			{
				key: 'montoVigente',
				label: 'M. Vigente',
				align: 'center',
				getValue: (r) => formatMoney(r.montoVigente)
			},
			{
				key: 'vigente%',
				label: '% Vigente',
				align: 'center',
				getValue: (r) => ((r.vigente / r.noCredits) * 100).toFixed(2)
			},
			{
				key: 'vencido',
				label: 'Vencido',
				align: 'center',
				getValue: (r) => r.vencido
			},
			{
				key: 'montoVencido',
				label: 'M. Vencido',
				align: 'center',
				getValue: (r) => formatMoney(r.montoVencido)
			},
			{
				key: 'vencido%',
				label: '% Vencido',
				align: 'center',
				getValue: (r) => ((r.vencido / r.noCredits) * 100).toFixed(2)
			},
			{
				key: 'creditoPromedio',
				label: 'Credito Prom.',
				align: 'center',
				getValue: (r) => formatMoney(r.creditoPromedio)
			},
			{
				key: 'saldoCartera',
				label: 'Saldo Cartera',
				align: 'center',
				getValue: (r) => formatMoney(r.saldoCartera)
			}
		];

		return (
			<>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								{columns.map((col) => (
									<TableCell
										key={col.key}
										align={col.align}
										style={{ fontSize: headerFontSize }}
										className="text-primary fw-bold"
									>
										{col.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{dataGestor.map((r) => {
								return (
									<TableRow key={r.id}>
										{columns.map((col) => {
											if (col.key === 'estadoCuotas') {
												return (
													<TableCell
														key={col.key}
														align={col.align}
														style={{ fontSize: dataFontSize }}
														className={isForPrint ? 'text-dark' : ''}
													>
														<Stack>
															<span
																style={{
																	color: '#00a152'
																}}
															>{`Pagadas: ${
																col.getValue(r).cuotasPagadas
															}`}</span>
															<span
																style={{
																	color: '#f50057'
																}}
															>{`Pendientes: ${
																col.getValue(r).cuotasPendiente
															}`}</span>
														</Stack>
													</TableCell>
												);
											} else {
												return (
													<TableCell
														key={col.key}
														align={col.align}
														style={{ fontSize: dataFontSize }}
														className={isForPrint ? 'text-dark' : ''}
													>
														{col.getValue(r)}
													</TableCell>
												);
											}
										})}
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</>
		);
	};

	const consolidatedTotals = useMemo(() => {
		if (!data.length) {
			return {
				totalClientsConsol: 0,
				totalCreditsConsol: 0,

				totalAlDiaConsol: 0,
				totalMontoAlDiaConsol: 0,

				totalMoraConsol: 0,
				totalMontoMoraConsol: 0,

				totalVigenteConsol: 0,
				totalMontoVigenteConsol: 0,

				totalvencidoConsol: 0,
				totalMontovencidoConsol: 0,

				creditoPromConsol: 0,
				totalSaldoConsol: 0
			};
		}

		return data.reduce(
			(acc, c) => {
				acc.totalClientsConsol += c.noClients;
				acc.totalCreditsConsol += c.noCredits;

				acc.totalAlDiaConsol += c.dia;
				acc.totalMontoAlDiaConsol += c.montoDia;

				acc.totalMoraConsol += c.mora;
				acc.totalMontoMoraConsol += c.montoMora;

				acc.totalVigenteConsol += c.vigente;
				acc.totalMontoVigenteConsol += c.montoVigente;

				acc.totalvencidoConsol += c.vencido;
				acc.totalMontovencidoConsol += c.montoVencido;

				let prom = 0;
				prom += c.creditoPromedio;

				acc.creditoPromConsol = prom / data.length;
				acc.totalSaldoConsol += c.saldoCartera;

				return acc;
			},
			{
				totalClientsConsol: 0,
				totalCreditsConsol: 0,

				totalAlDiaConsol: 0,
				totalMontoAlDiaConsol: 0,

				totalMoraConsol: 0,
				totalMontoMoraConsol: 0,

				totalVigenteConsol: 0,
				totalMontoVigenteConsol: 0,

				totalvencidoConsol: 0,
				totalMontovencidoConsol: 0,

				creditoPromConsol: 0,
				totalSaldoConsol: 0
			}
		);
	}, [data]);

	const {
		totalClientsConsol,
		totalCreditsConsol,

		totalAlDiaConsol,
		totalMontoAlDiaConsol,

		totalMoraConsol,
		totalMontoMoraConsol,

		totalVigenteConsol,
		totalMontoVigenteConsol,

		totalvencidoConsol,
		totalMontovencidoConsol,

		creditoPromConsol,
		totalSaldoConsol
	} = consolidatedTotals;

	const footer = (
		<Stack>
			<Typography
				sx={{
					color: '#2196f3',
					textAlign: 'center'
				}}
			>
				Consolidado
			</Typography>

			<hr />

			<Stack direction="row" flex="row" justifyContent="space-around">
				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Clientes
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalClientsConsol}</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Creditos
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalCreditsConsol}</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Creditos Al Dia
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalAlDiaConsol}</Typography>
				</Stack>
				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Monto Creditos al Dia
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(totalMontoAlDiaConsol)}
					</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Creditos Mora
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalMoraConsol}</Typography>
				</Stack>
				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Monto Creditos en Mora
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(totalMontoMoraConsol)}
					</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Creditos Vigentes
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalVigenteConsol}</Typography>
				</Stack>
				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Monto Creditos Vigentes
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(totalMontoVigenteConsol)}
					</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Total Creditos Vencidos
					</Typography>
					<Typography style={{ fontSize: 11 }}>{totalvencidoConsol}</Typography>
				</Stack>
				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Monto Creditos Vencidos
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(totalvencidoConsol)}
					</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Monto Creditos Promedio
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(creditoPromConsol)}
					</Typography>
				</Stack>

				<Stack textAlign="center">
					<Typography
						style={{
							fontWeight: 'bold',
							color: '#03a9f4',
							fontSize: 11
						}}
					>
						Monto Saldo Cartera
					</Typography>
					<Typography style={{ fontSize: 11 }}>
						{new Intl.NumberFormat('es-NI', {
							style: 'currency',
							currency: 'NIO'
						}).format(totalSaldoConsol)}
					</Typography>
				</Stack>
			</Stack>
			<hr />
		</Stack>
	);

	return (
		<div>
			<Dialog fullScreen open={true}>
				<AppBar sx={{ position: 'relative' }}>
					<Toolbar>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
						<Typography
							sx={{ ml: 2, flex: 1, textAlign: 'center' }}
							variant="h5"
							component="div"
						>
							{`${title} `}
						</Typography>
						<img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
					</Toolbar>
				</AppBar>

				<Stack display="flex" justifyContent="center">
					<Stack
						direction={'row'}
						justifyContent="space-between"
						paddingLeft={5}
						paddingRight={5}
						marginTop={1}
						marginBottom={1}
					>
						<IconButton variant="outlined" onClick={() => exportExcel()}>
							<FontAwesomeIcon
								icon={faCloudArrowDown}
								style={{ fontSize: 40, color: '#00a152', width: 40 }}
							/>
						</IconButton>

						<Stack>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
							>
								{slogan}
							</Typography>
							<Typography
								sx={{
									color: '#2196f3',
									textAlign: 'center',
									fontWeight: 'bold'
								}}
								variant="h6"
								component="div"
							>
								Reporte de Saldos Cartera Por Gestor
							</Typography>
						</Stack>

						<ReactToPrint
							trigger={() => {
								return (
									<IconButton
										variant="outlined"
										// style={{ position: "fixed", right: 20, top: 75 }}
									>
										<PrintRoundedIcon
											style={{ fontSize: 40, color: '#2979ff', width: 40 }}
										/>
									</IconButton>
								);
							}}
							content={() => compRef.current}
							pageStyle={pageStyle}
						/>
					</Stack>

					{paramText}
				</Stack>

				<Container maxWidth={false} sx={{ textAlign: 'center' }}>
					<hr />
					{isEmpty(data) ? (
						<NoData />
					) : (
						<>
							<Divider />

							<DataTable dataGestor={data} headerFontSize={11} dataFontSize={10} />

							{gestorList.length === 1 ? null : footer}
						</>
					)}
				</Container>
			</Dialog>

			<div
				style={{
					display: 'none'
				}}
			>
				<PrintReport
					ref={compRef}
					titulo={'Reporte de Saldos Cartera Por Gestor'}
					fecha={`Fecha: ${moment().format('L')}`}
				>
					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						{paramText}
					</Container>

					<Container maxWidth={false} sx={{ textAlign: 'center' }}>
						<hr />

						{isEmpty(data) ? (
							<NoData />
						) : (
							<>
								<Divider />

								<DataTable
									dataGestor={data}
									headerFontSize={9}
									dataFontSize={8}
									isForPrint={true}
								/>

								{gestorList.length === 1 ? null : footer}
							</>
						)}
					</Container>
				</PrintReport>
			</div>
		</div>
	);
};

export default SaldosCarteraByGestor;
